import React, { useState, useEffect, useCallback } from "react";
import { ImageThumbnail } from "../../../components/ImageThumbnail";
import { Folder } from "../../../components/Folder";
import { FolderBreadcrumb } from "../../../components/FolderBreadcrumb";
import { FolderSelectModal } from "../../../components/FolderSelectModal";
import { ConfirmationModal } from "../../../components/_ui/ConfirmationModal";
import { SelectedFile, UpdateFile } from "../../../types/PostSelection";
import {
  getFoldersWithCounts,
  createFolder,
  deleteFolder,
  renameFolder,
  moveFilesToFolder,
  moveFolder,
  FolderWithCounts,
} from "../../../api/folders";
import { handleError } from "../../../utils/handleError";
import "./styles.css";
import { PhotoViewer } from "../../../components/PhotoViewer";
import { Grid } from "react-feather";

export function FilePicker({
  files,
  updateFile,
  createPost,
  editMode,
  lastFileElementRef,
  isLoading,
  currentFolder = "",
  onFolderChange,
  showCreateFolder,
  setShowCreateFolder,
  showFolderSelectModal,
  setShowFolderSelectModal,
}: {
  files: SelectedFile[];
  updateFile: UpdateFile;
  createPost: (value: SelectedFile[]) => void;
  editMode: boolean;
  lastFileElementRef?: (node: HTMLDivElement) => void;
  isLoading?: boolean;
  currentFolder?: string;
  onFolderChange?: (folderPath: string) => void;
  showCreateFolder: boolean;
  setShowCreateFolder: React.Dispatch<React.SetStateAction<boolean>>;
  showFolderSelectModal: boolean;
  setShowFolderSelectModal: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [preview, setPreview] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [folders, setFolders] = useState<FolderWithCounts[]>([]);

  const [newFolderName, setNewFolderName] = useState("");
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<
    { name: string; path: string }[]
  >([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(
    null
  );
  const [folderToMove, setFolderToMove] = useState<string | null>(null);
  const [showFolderMoveModal, setShowFolderMoveModal] = useState(false);

  const loadFolders = useCallback(async () => {
    try {
      const folderData = await getFoldersWithCounts(currentFolder);
      setFolders(folderData);
    } catch (error) {
      handleError(error as Error);
    }
  }, [currentFolder]);

  const loadBreadcrumbs = useCallback(() => {
    if (!currentFolder) {
      setBreadcrumbs([]);
      return;
    }

    const pathParts = currentFolder.split("/");
    const breadcrumbItems = pathParts.map((part, index) => ({
      name: part,
      path: pathParts.slice(0, index + 1).join("/"),
    }));
    setBreadcrumbs(breadcrumbItems);
  }, [currentFolder]);

  useEffect(() => {
    const loadAll = async () => {
      await loadFolders();
      loadBreadcrumbs();
    };
    loadAll();
  }, [loadFolders, loadBreadcrumbs]);

  // Reset last selected index when files change (new files loaded or folder changed)
  useEffect(() => {
    setLastSelectedIndex(null);
  }, [files.length, currentFolder]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      await createFolder(newFolderName.trim(), currentFolder);
      setNewFolderName("");
      setShowCreateFolder(false);
      loadFolders();
    } catch (error) {
      handleError(error as Error);
    }
  };

  const handleDeleteFolder = async (folderPath: string) => {
    setFolderToDelete(folderPath);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteFolder = async () => {
    if (!folderToDelete) return;

    try {
      const folder = folders.find((f) => f.path === folderToDelete);
      if (folder) {
        await deleteFolder(folder.id);
        loadFolders();
      }
    } catch (error) {
      handleError(error as Error);
    } finally {
      setShowDeleteConfirmation(false);
      setFolderToDelete(null);
    }
  };

  const handleRenameFolder = async (folderPath: string, newName: string) => {
    try {
      const folder = folders.find((f) => f.path === folderPath);
      if (folder) {
        await renameFolder(folder.id, newName);
        loadFolders();
      }
    } catch (error) {
      handleError(error as Error);
    }
  };

  const handleFolderEnter = (folderPath: string) => {
    if (onFolderChange) {
      setLastSelectedIndex(null); // Reset selection index when changing folders
      onFolderChange(folderPath);
    }
  };

  const handleNavigate = (path: string) => {
    if (onFolderChange) {
      setLastSelectedIndex(null); // Reset selection index when navigating
      onFolderChange(path);
    }
  };

  const handleMoveToFolder = async (targetFolderPath: string) => {
    try {
      if (folderToMove) {
        // Moving a folder
        const folder = folders.find((f) => f.path === folderToMove);
        if (folder) {
          // Validate that we're not moving the folder into itself or its subdirectories
          if (
            targetFolderPath === folder.path ||
            targetFolderPath.startsWith(folder.path + "/")
          ) {
            const toast = await import("react-hot-toast");
            toast.default.error(
              "Cannot move a folder into itself or its subdirectories"
            );
            return;
          }

          await moveFolder(folder.id, targetFolderPath);

          // Show success message
          const toast = await import("react-hot-toast");
          const targetFolderName = targetFolderPath || "Root";
          toast.default.success(
            `Moved folder "${folder.name}" to ${targetFolderName}`
          );

          // Refresh the folder list
          loadFolders();
        }
        setFolderToMove(null);
        setShowFolderMoveModal(false);
      } else {
        // Moving files (existing functionality)
        const selectedFiles = files.filter((f) => f.isSelected);

        // Import getMediaPath to construct correct paths
        const { getMediaPath, MEDIA_SIZE } = await import("../../../api/files");
        const originalPath = await getMediaPath(MEDIA_SIZE.original);

        const filePaths = selectedFiles.map((f) => `${originalPath}/${f.name}`);

        await moveFilesToFolder(filePaths, targetFolderPath);

        // Show success message
        const toast = await import("react-hot-toast");
        const folderName = targetFolderPath || "Root";
        toast.default.success(
          `Moved ${selectedFiles.length} file${
            selectedFiles.length > 1 ? "s" : ""
          } to ${folderName}`
        );

        // Reset selections and refresh if we moved files out of current folder
        if (onFolderChange) {
          onFolderChange(currentFolder);
        }

        // Clear selections
        selectedFiles.forEach((file) => {
          updateFile(file.name, { isSelected: false });
        });
      }
    } catch (error) {
      handleError(error as Error);
    }
  };

  const handleMoveFolderRequest = (folderPath: string) => {
    setFolderToMove(folderPath);
    setShowFolderMoveModal(true);
  };

  /**
   * Handles image selection with support for shift+click range selection
   * - Normal click: toggles selection of single image
   * - Shift+click: selects all images between last selected and current
   */
  const handleImageSelect = (index: number, event?: React.MouseEvent) => {
    const img = files[index];

    if (
      event?.shiftKey &&
      lastSelectedIndex !== null &&
      lastSelectedIndex !== index
    ) {
      // Range selection with shift+click
      const startIndex = Math.min(lastSelectedIndex, index);
      const endIndex = Math.max(lastSelectedIndex, index);
      const currentTime = Date.now();

      // Select all images in the range
      for (let i = startIndex; i <= endIndex; i++) {
        const file = files[i];
        if (!file.isSelected) {
          updateFile(file.name, {
            isSelected: true,
            selectedAt: currentTime + (i - startIndex), // Ensure proper ordering
          });
        }
      }

      setLastSelectedIndex(index);
    } else {
      // Normal single selection
      const isCurrentlySelected = img.isSelected;
      updateFile(img.name, {
        isSelected: !isCurrentlySelected,
        selectedAt: isCurrentlySelected ? undefined : Date.now(),
      });

      // Update last selected index only if we're selecting (not deselecting)
      if (!isCurrentlySelected) {
        setLastSelectedIndex(index);
      } else if (isCurrentlySelected) {
        // If we're deselecting and this was the last selected, clear it
        if (lastSelectedIndex === index) {
          setLastSelectedIndex(null);
        }
      }
    }
  };

  const close = () => {
    setPreview(false);
    setSelected(null);
  };

  const sorted = files
    .filter((f) => f.isSelected)
    .sort((a, b) =>
      a?.selectedAt && b?.selectedAt ? a.selectedAt - b.selectedAt : 0
    );

  return (
    <>
      <div className="file-picker overflow-y-auto overflow-x-hidden flex-wrap pt-4 px-3">
        <div>
          {breadcrumbs.length > 0 && (
            <div className="mb-2">
              <FolderBreadcrumb
                items={breadcrumbs}
                onNavigate={handleNavigate}
              />
            </div>
          )}

          {folders.length > 0 && (
            <h3 className="text-sm font-medium mb-3 text-base-content/70">
              Folders
            </h3>
          )}

          {showCreateFolder && (
            <div className="mb-4 flex gap-2 items-center">
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="Folder name"
                  className="input input-sm"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreateFolder();
                    if (e.key === "Escape") {
                      setShowCreateFolder(false);
                      setNewFolderName("");
                    }
                  }}
                  autoFocus
                />
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleCreateFolder}
                  disabled={!newFolderName.trim()}
                >
                  Create
                </button>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => {
                    setShowCreateFolder(false);
                    setNewFolderName("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {folders.length > 0 && (
            <div className=" mb-6">
              {folders.map((folder) => (
                <Folder
                  key={folder.id}
                  name={folder.name}
                  path={folder.path}
                  totalFiles={folder.totalFiles}
                  usedFiles={folder.usedFiles}
                  onEnter={handleFolderEnter}
                  onRename={handleRenameFolder}
                  onDelete={handleDeleteFolder}
                  onMove={handleMoveFolderRequest}
                />
              ))}
            </div>
          )}
        </div>

        {/* Files Grid */}
        {files.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-3 text-base-content/70">
              Files
            </h3>
            <div className="file-picker-grid grid gap-4 grid-cols-3 min-[500px]:grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-3 xl:grid-cols-4">
              {files.map((img, index) => (
                <div
                  key={img.id + img.name}
                  ref={
                    index === files.length - 1 ? lastFileElementRef : undefined
                  }
                >
                  <ImageThumbnail
                    id={img.id!}
                    title={img.name}
                    url={img.url}
                    minetype={img.mine_type}
                    rating={img.rating}
                    isUsed={img.isUsed}
                    isSelected={img.isSelected}
                    selectedOrder={
                      img.isSelected &&
                      sorted.findIndex((f) => f.selectedAt === img.selectedAt) +
                        1
                    }
                    isUploading={img.isUploading}
                    onSelect={(event?: React.MouseEvent) =>
                      handleImageSelect(index, event)
                    }
                    onViewerSelection={() => {
                      console.log("VIEW IMAGE", img);
                      setPreview(true);
                      setSelected(img._id ?? null);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center w-full py-4">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        )}
      </div>

      <div className="px-3 py-6 flex gap-2 w-full">
        <button
          className="btn btn-outline"
          onClick={() => {
            setPreview(true);
            setSelected(files[0]?._id ?? null);
          }}
        >
          <Grid size={20} />
        </button>
        <button
          className="btn btn-primary flex-1"
          disabled={sorted.length === 0}
          onClick={() => createPost(sorted)}
        >
          {editMode ? "Update" : "Create"} a post
        </button>
      </div>

      {preview && (
        <PhotoViewer
          images={files}
          open={preview}
          selected={selected}
          close={close}
          onCreatePost={(photos) => {
            setPreview(false);
            createPost(photos);
          }}
        />
      )}

      {showFolderSelectModal && (
        <FolderSelectModal
          isOpen={showFolderSelectModal}
          onClose={() => setShowFolderSelectModal(false)}
          onSelectFolder={handleMoveToFolder}
          currentFolder={currentFolder}
        />
      )}

      {showFolderMoveModal && (
        <FolderSelectModal
          isOpen={showFolderMoveModal}
          onClose={() => {
            setShowFolderMoveModal(false);
            setFolderToMove(null);
          }}
          onSelectFolder={handleMoveToFolder}
          currentFolder={folderToMove || ""}
          title={`Move Folder "${
            folders.find((f) => f.path === folderToMove)?.name || folderToMove
          }"`}
          excludeFolder={folderToMove || undefined}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setFolderToDelete(null);
        }}
        onConfirm={confirmDeleteFolder}
        title="Delete Folder"
        message={`Are you sure you want to delete the folder "${
          folderToDelete
            ? folders.find((f) => f.path === folderToDelete)?.name ||
              folderToDelete
            : ""
        }"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
}
